import React, { useEffect, useState } from "react";

import HttpManager from "../../services/HttpManager";
import { ArticleDTO } from "../../models/ArticleDTO";
import { DeliveryPlan } from "../../services/v2/inventory.service";
import { kitDeliveryStatus } from "../../constants";
import { FIELD_FONT, requestErrorText } from "./kitUi";
import FirmaPad from "./firmaPad";
import FotoEntrega from "./fotoEntrega";
import PiecesPicker, { PiecePicks, pendingLinesOf, piecesItems, piecesValid, presetPicks } from "./piecesPicker";
import EntregaVentaVieja, { LegacyDraft, emptyLegacy, legacyItems, legacyValid } from "./entregaVentaVieja";

/** What the student leaves when receiving the product (Oct 2026): hand signature (PNG data URL) and optional photo. */
export interface DeliveryProof {
	signature: string;
	photo: File | null;
}

export type DeliveryAction =
	| ({ kind: "delivery"; type: "partial" | "total"; items?: { articleId: number; quantity: number }[]; previous?: string } & DeliveryProof)
	| { kind: "status"; deliveryStatusId: number }
	| { kind: "note" }
	| ({ kind: "items"; items: { componentId: number | null; articleId: number; quantity: number; serials?: string[] }[] } & DeliveryProof);

export interface DeliveryPromptProps {
	kit: ArticleDTO;
	campusId: number;
	busy?: boolean;
	error?: string | null;
	/** CREATE bit of "kits": entregar (pieza por pieza o simple) y notas. Default true. */
	canCreate?: boolean;
	/** UPDATE bit of "kits": corregir el estado ("Ya Se Había Entregado Antes"). Default true. */
	canUpdate?: boolean;
	onConfirm: (action: DeliveryAction, note: string, warehouseId?: number | null) => void;
	onCancel: () => void;
}

/**
 * "Registrar Entrega". Cuando el producto ya está en el catálogo nuevo, se entrega pieza por pieza:
 * se marca qué salió, con qué talla o sustituto, y el inventario se descuenta al guardar.
 * Los kits vendidos con el catálogo viejo mantienen la entrega simple (parcial / completa / nota).
 * Si el plan no carga se enseña el motivo y no se ofrece la entrega simple (sería "Entregado" sin descontar inventario).
 * Un kit "Sin Registro" (vendido antes de sep-2026) de un producto que ya está en el catálogo nuevo puede cerrarse como
 * "Ya Se Había Entregado Antes", que no descuenta inventario (esas piezas salieron hace meses).
 * Si es del catálogo viejo, quien registra entregas también puede cerrarlo como entrega completa de hoy, con firma (Esteban, 2026-10-29).
 * Toda entrega (parcial, completa o por piezas) lleva la firma del alumno y una foto opcional; el backend arma el
 * Comprobante de Entrega y lo manda por WhatsApp de Caja (Esteban, 2026-10-26). Nota sola y corrección de estado no firman.
 */
const DeliveryPrompt: React.FC<DeliveryPromptProps> = ({ kit, campusId, busy, error, canCreate = true, canUpdate = true, onConfirm, onCancel }) => {
	const unknown = kit.deliveryStatusId === kitDeliveryStatus.UNKNOWN;
	/** Esteban, 2026-09-30: nada se entrega (ni parcial) hasta que está pagado completo; la nota sí se puede dejar. */
	const paidInFull = kit.statusId === 2 || kit.statusId === 3;
	const [plan, setPlan] = useState<DeliveryPlan | null>(null);
	const [loadingPlan, setLoadingPlan] = useState<boolean>(true);
	const [planError, setPlanError] = useState<string | null>(null);
	const [reloadKey, setReloadKey] = useState<number>(0);
	const [choice, setChoice] = useState<string>(!paidInFull ? (canCreate ? "note" : "") : unknown ? "" : (canCreate ? "total" : ""));
	/** Kit "Sin Registro" del catálogo nuevo: "pieces" = entregar ahora y descontar · "already" = ya se había entregado antes. */
	const [unknownMode, setUnknownMode] = useState<"pieces" | "already" | null>(null);
	const [note, setNote] = useState<string>("");
	const [picked, setPicked] = useState<PiecePicks>({});
	/** De qué bodega sale. Online no tiene bodega propia: elige entre las de su país. */
	const [warehouseId, setWarehouseId] = useState<number | null>(null);
	/** Firma del alumno (PNG data URL) y foto opcional de la entrega. */
	const [signature, setSignature] = useState<string | null>(null);
	const [photo, setPhoto] = useState<File | null>(null);
	/** Old-catalog sale: what was given before + today's new-catalog products (they leave the store). */
	const [legacyDraft, setLegacyDraft] = useState<LegacyDraft>(emptyLegacy());

	useEffect(() => {
		let alive = true;
		setLoadingPlan(true);
		setPlanError(null);
		HttpManager.getInstance().inventoryService.deliveryPlan(campusId, kit.id, warehouseId)
			.then((response) => {
				if (!alive) return;
				setPlan(response.data);
				if (warehouseId === null && response.data.warehouse) setWarehouseId(response.data.warehouse.id);
				setPicked(presetPicks(response.data));
				if (response.data.legacy && !response.data.tracksInventory) setChoice((current) => current === "total" ? "legacy" : current);
			})
			.catch((requestError) => {
				if (!alive) return;
				setPlan(null);
				setPlanError(requestErrorText(requestError, "No se pudo cargar lo que falta entregar."));
			})
			.finally(() => { if (alive) setLoadingPlan(false); });
		return () => { alive = false; };
	}, [campusId, kit.id, warehouseId, reloadKey]);

	useEffect(() => {
		const onKey = (event: KeyboardEvent) => { if (event.key === "Escape" && !busy) onCancel(); };
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [busy, onCancel]);

	const tracked = !!plan && plan.tracksInventory !== false && plan.lines.length > 0;
	const canAlready = tracked && unknown && paidInFull && canUpdate && !plan!.blockedReason;
	const canPieces = tracked && canCreate;
	// Sin Registro: hay que elegir primero entre entregar ahora o "ya se había entregado"; si solo hay un camino, ese.
	const mode: "pieces" | "already" | null = !tracked ? null : !unknown ? "pieces"
		: canAlready && canPieces ? unknownMode : canAlready ? "already" : "pieces";
	const byPieces = tracked && mode === "pieces";
	const already = tracked && mode === "already";
	const pendingLines = tracked ? pendingLinesOf(plan) : [];
	const trimmed = note.trim();
	const validPieces = piecesValid(plan, picked);
	const legacy = !tracked && !!plan && !!plan.legacy;
	/** "Sale de": only when the campus has more than one warehouse that delivers (TGU: Tienda/América, SPS: Tienda/Acacias, Online). */
	const warehousePicker = plan && (plan.warehouseOptions || []).length > 1 ? <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "10px", flexWrap: "wrap" }}>
		<span style={{ fontSize: "13px", fontWeight: 600 }}>Sale de</span>
		<select className="form-select" style={{ height: "34px", fontSize: FIELD_FONT, width: "auto", flex: "1 1 220px", minWidth: 0 }} disabled={busy}
			value={warehouseId === null ? "" : String(warehouseId)}
			onChange={(event) => setWarehouseId(Number(event.target.value))}>
			{(plan.warehouseOptions || []).map((warehouse) => <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>)}
		</select>
	</div> : null;
	const validSimple = choice === "legacy" ? legacyValid(legacyDraft, note) : choice !== "" && trimmed.length >= 3;
	const validAlready = trimmed.length >= 3;
	/** Entrega real (parcial, completa o por piezas): pide firma y manda comprobante. Nota y corrección de estado, no. */
	const needsProof = !loadingPlan && !planError && (byPieces
		? canCreate && !plan!.blockedReason && pendingLines.length > 0
		: !tracked && paidInFull && (choice === "partial" || choice === "total" || choice === "legacy"));
	const proofOk = !needsProof || !!signature;

	const confirm = () => {
		if (already) {
			if (!validAlready) return;
			onConfirm({ kind: "status", deliveryStatusId: kitDeliveryStatus.DELIVERED }, trimmed);
			return;
		}
		if (byPieces) {
			if (!validPieces) return;
			if (!signature) return;
			onConfirm({
				kind: "items",
				items: piecesItems(picked),
				signature,
				photo
			}, trimmed, warehouseId);
			return;
		}
		if (tracked || planError) return;
		if (!validSimple) return;
		if (choice === "legacy") {
			if (signature) onConfirm({ kind: "delivery", type: legacyDraft.type, items: legacyItems(legacyDraft), previous: legacyDraft.previous, signature, photo }, trimmed, plan ? plan.warehouse?.id : null);
		}
		else if (choice === "partial" || choice === "total") { if (signature) onConfirm({ kind: "delivery", type: choice, signature, photo }, trimmed); }
		else if (choice === "was_delivered") onConfirm({ kind: "status", deliveryStatusId: kitDeliveryStatus.DELIVERED }, trimmed);
		else if (choice === "note") onConfirm({ kind: "note" }, trimmed);
	};

	const option = (value: string, title: string, hint: string) => card(choice === value, () => setChoice(value), title, hint, value);

	const card = (selected: boolean, onPick: () => void, title: string, hint: string, cardKey?: string) => {
		return <div key={cardKey || title} role="radio" aria-checked={selected} onClick={() => { if (!busy) onPick(); }}
			style={{ display: "flex", gap: "10px", alignItems: "flex-start", padding: "8px 10px", border: "1px solid " + (selected ? "var(--wa-acento)" : "var(--wa-filo)"), borderRadius: "8px", marginBottom: "6px", cursor: busy ? "default" : "pointer", background: selected ? "var(--wa-acento-suave)" : "var(--wa-superficie)" }}>
			<span style={{ flex: "0 0 16px", width: "16px", height: "16px", marginTop: "2px", borderRadius: "50%", border: "2px solid " + (selected ? "var(--wa-acento)" : "var(--wa-filo-3)"), background: selected ? "var(--wa-acento)" : "var(--wa-superficie)", boxShadow: selected ? "inset 0 0 0 3px var(--wa-superficie)" : "none" }} />
			<span style={{ flex: 1 }}>
				<span style={{ display: "block", fontWeight: 600, fontSize: "14px", color: "var(--wa-tinta)" }}>{title}</span>
				{hint ? <span style={{ display: "block", fontSize: "12px", color: "var(--wa-apagado)" }}>{hint}</span> : null}
			</span>
		</div>;
	};

	const enrollment = (kit.enrollments || [])[0];

	return <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
		onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onCancel(); }}
	>
		<div className="card" style={{ width: "100%", maxWidth: byPieces ? "680px" : "560px", maxHeight: "92vh", overflowY: "auto", boxShadow: "0 10px 40px rgba(0,0,0,0.25)" }} role="dialog" aria-modal="true">
			<div className="card-body">
				<h4 style={{ marginBottom: "8px" }}>Registrar Entrega</h4>
				<div style={{ background: "var(--wa-papel-2)", borderRadius: "8px", padding: "10px 12px", marginBottom: "12px", fontSize: "13px", lineHeight: 1.5 }}>
					<div style={{ fontSize: "15px", fontWeight: 700, color: "var(--wa-tinta)" }}>{kit.name}</div>
					<div style={{ color: "var(--wa-apagado)", fontSize: "11px" }}>SKU {kit.sku}{kit.quantity > 1 ? " · " + kit.quantity + " unidades" : ""}</div>
					{kit.isThirdParty
						? <div style={{ marginTop: "4px" }}><b>{kit.buyerName || "Sin datos del comprador"}</b><span style={{ color: "var(--wa-apagado)" }}> · Venta a terceros{kit.buyerPhone ? " · " + kit.buyerPhone : ""}</span></div>
						: kit.studentName ? <div style={{ marginTop: "4px" }}><b>{kit.studentName}</b>{kit.studentCode ? <span style={{ color: "var(--wa-apagado)" }}> · {kit.studentCode}</span> : null}</div> : null}
					{enrollment ? <div style={{ color: "var(--wa-apagado)" }}>{enrollment.courseBrief || enrollment.module || enrollment.level}{enrollment.period ? " · " + enrollment.period : ""}{enrollment.schedule ? " · " + enrollment.schedule : ""}{enrollment.mentor ? " · " + enrollment.mentor : ""}</div> : null}
					<div style={{ marginTop: "4px" }}>
						Pago: <b>{kit.paidAmount.moneyFormat}</b> de {kit.subTotal.moneyFormat}
						{kit.isDebt ? <span style={{ color: "var(--wa-oro)" }}> · debe {kit.pendingAmount.moneyFormat}</span> : null}

					</div>
				</div>

				{loadingPlan ? <div style={{ padding: "16px", textAlign: "center", color: "var(--wa-apagado)" }}>Cargando lo que falta entregar…</div>
					: planError ? <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--wa-oro)", background: "var(--wa-oro-suave)", borderRadius: "8px", padding: "8px 10px", marginBottom: "10px" }}>
						<div>No se puede registrar la entrega: {planError}</div>
						<button type="button" className="btn btn-outline-secondary btn-sm rounded-pill" style={{ marginTop: "6px" }} disabled={busy} onClick={() => setReloadKey(reloadKey + 1)}>Reintentar</button>
					</div>
					: tracked ? <>
						{plan!.blockedReason ? <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--wa-oro)", background: "var(--wa-oro-suave)", borderRadius: "8px", padding: "8px 10px", marginBottom: "10px" }}>
							{plan!.blockedReason}
						</div> : null}
						{unknown && canAlready && canPieces ? <>
							<div style={{ fontSize: "12px", color: "var(--wa-apagado)", marginBottom: "6px" }}>Este kit se vendió antes del control de entregas (Sin Registro).</div>
							{card(unknownMode === "already", () => setUnknownMode("already"), "Ya Se Había Entregado Antes (No Descuenta)", "")}
							{card(unknownMode === "pieces", () => setUnknownMode("pieces"), "Entregar Ahora y Descontar", "")}
						</> : null}
						{already && !(canAlready && canPieces) ? <div style={{ fontSize: "13px", marginBottom: "6px" }}>
							<b>Ya Se Había Entregado Antes (No Descuenta)</b>
						</div> : null}
						{!canCreate && !already ? <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--wa-oro)", background: "var(--wa-oro-suave)", borderRadius: "8px", padding: "8px 10px", marginBottom: "10px" }}>
							Tu rol no tiene permiso de registrar entregas en Control de Entregas.
						</div> : null}
						{byPieces ? <>
						{warehousePicker}
						<div style={{ fontWeight: 600, fontSize: "13px", marginBottom: "6px" }}>Marca lo que estás entregando</div>
						<PiecesPicker plan={plan!} picked={picked} onChange={setPicked} busy={busy} />
						</> : null}
						{plan!.deliveredItems.length ? <div style={{ marginTop: "10px" }}>
							<div style={{ fontWeight: 600, fontSize: "13px" }}>Ya entregado</div>
							{plan!.deliveredItems.map((item) => <div key={item.id} style={{ fontSize: "12px", color: "var(--wa-apagado)" }}>
								{item.createdAt} · {item.name}{item.sizeValue ? " talla " + item.sizeValue : ""} ×{item.quantity} · {item.author}
							</div>)}
						</div> : null}
					</>
					: <>
						{legacy ? <div style={{ fontSize: "12px", color: "var(--wa-apagado)", background: "var(--wa-papel-2)", borderRadius: "8px", padding: "8px 10px", marginBottom: "10px" }}>
							Venta del catálogo viejo: escribe lo que se le entregó antes y agrega lo que se lleva hoy. Lo de hoy sale de la tienda.
							{plan!.deliveredItems.length ? <div style={{ marginTop: "6px" }}>
								<b>Ya entregado en WAPP:</b> {plan!.deliveredItems.map((item) => item.name + " ×" + item.quantity + " (" + item.createdAt + ")").join(" · ")}
							</div> : null}
						</div> : null}
						{!paidInFull ? <>
							<div style={{ fontSize: "13px", fontWeight: 600, color: "var(--wa-oro)", background: "var(--wa-oro-suave)", borderRadius: "8px", padding: "8px 10px", marginBottom: "10px" }}>
								Se entrega cuando está pagado completo.
							</div>
							{canCreate ? option("note", "Agregar Nota de Entrega", "") : null}
						</> : unknown
							? <>
								{canCreate ? (legacy ? option("legacy", "Registrar Entrega", "")
									: option("total", "Se Entregó Completo Ahora", "")) : null}
								{canUpdate ? option("was_delivered", "Ya Se Había Entregado Antes", "") : null}
								{canCreate ? option("note", "Agregar Nota de Entrega", "") : null}
							</>
							: <>
								{canCreate ? (legacy ? option("legacy", "Registrar Entrega", "")
									: option("total", "Se Entregó Completo", "")) : null}
								{canCreate ? option("note", "Agregar Nota de Entrega", "") : null}
							</>}
					</>}

				{!loadingPlan && !planError && legacy && choice === "legacy" && paidInFull
					? <>{warehousePicker}<EntregaVentaVieja campusId={campusId} warehouse={plan!.warehouse || null} value={legacyDraft} onChange={setLegacyDraft} busy={busy} /></> : null}
				{!loadingPlan && !planError && (!tracked || byPieces || already) ? <>
					<div style={{ fontWeight: 600, fontSize: "13px", marginTop: "10px", marginBottom: "4px" }}>
						{byPieces || choice === "legacy" ? "Nota (opcional)" : already ? "¿Cuándo o cómo se entregó?" : choice === "note" ? "Nota" : "Detalle de la entrega"}
						{byPieces || choice === "legacy" ? null : <span style={{ color: "var(--wa-oro)" }}> *</span>}
					</div>
					<textarea className="form-control" rows={2} disabled={busy} value={note} maxLength={2000}
						placeholder={byPieces ? "Algo que valga la pena recordar de esta entrega." : already ? "Ej.: Se le entregó completo en julio, antes del conteo." : "Ej.: Se le entregó en Caja."}
						onChange={(event) => setNote(event.target.value)} style={{ resize: "vertical", fontSize: "16px" }} />
				</> : null}

				{needsProof ? <>
					<div style={{ fontWeight: 600, fontSize: "13px", marginTop: "12px", marginBottom: "6px" }}>{kit.isThirdParty ? "Firma de Quien Recibe" : "Firma del Alumno"} <span style={{ color: "var(--wa-oro)" }}>*</span></div>
					<FirmaPad value={signature} onChange={setSignature} disabled={busy} studentName={kit.isThirdParty ? kit.buyerName : kit.studentName} />
					<div style={{ fontWeight: 600, fontSize: "13px", marginTop: "12px", marginBottom: "6px" }}>Foto o Comprobante (Opcional)</div>
					<FotoEntrega value={photo} onChange={setPhoto} disabled={busy} />
				</> : null}

				{error ? <div className="alert alert-danger" style={{ marginTop: "10px", padding: "8px 12px", fontSize: "14px" }}>{error}</div> : null}
				{/* Phones: the primary button keeps one line and takes the row; Cancelar stays small next to it or above. */}
				<div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "8px", marginTop: "14px", flexWrap: "wrap" }}>
					<button type="button" className="btn btn-outline-secondary rounded-pill" disabled={busy} onClick={onCancel} style={{ flex: "0 0 auto" }}>Cancelar</button>
					<button type="button" className="btn btn-primary rounded-pill" onClick={confirm} style={{ flex: "0 1 auto", whiteSpace: "nowrap", maxWidth: "100%" }}
						disabled={busy || loadingPlan || !!planError || !proofOk || (already ? !validAlready : byPieces ? !canCreate || !validPieces || !!plan!.blockedReason : tracked ? true : !validSimple)}>
						{busy ? "Guardando…" : needsProof ? "Guardar y Enviar Comprobante" : byPieces ? "Entregar y Descontar" : "Guardar"}
					</button>
				</div>
			</div>
		</div>
	</div>;
};

export default DeliveryPrompt;
